import {
  createContext,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useReducer,
  useRef,
} from 'react';
import { WorkflowGraph } from '../graph';
import { ContextMenuService } from '../services/contextMenu';
import type { GraphEditorProviderProps, GraphEditorRef } from './types';
import { useIsDirty, useGraphEditorStore } from '../graph-store';
import { useBeforeUnload } from '../hooks/useBeforeUnload';
import { useToast } from '../hooks/useToast';

export interface GraphEditorContextValue {
  graph: WorkflowGraph;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export const GraphEditorContext =
  createContext<GraphEditorContextValue | null>(null);

export const GraphEditorProvider = forwardRef<
  GraphEditorRef,
  GraphEditorProviderProps
>(function GraphEditorProvider(
  { config, initialSnapshot, onReady, containerStyle, containerClassName, children },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<WorkflowGraph | null>(null);
  const contextMenuServiceRef = useRef<ContextMenuService | null>(null);
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  const { showWarning, showSuccess } = useToast();

  // Warn the user before closing/navigating away with unsaved changes
  const isDirty = useIsDirty();
  useBeforeUnload(isDirty);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const graph = new WorkflowGraph({
      container: containerRef.current,
      ...config,
    });

    if (initialSnapshot) {
      graph.hydrateFromSnapshot(initialSnapshot);
    }

    graphRef.current = graph;
    forceRender();

    onReady?.(graph);
    contextMenuServiceRef.current = new ContextMenuService(graph, { showWarning, showSuccess });

    return () => {
      contextMenuServiceRef.current?.destroy();
      contextMenuServiceRef.current = null;
      graph.destroy();
      graphRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

// cells-folded is handled by wireGraphToStore (_applyGroupCollapsed) — no duplicate listener needed.

  useImperativeHandle(
    ref,
    () => ({
      getGraph: () => graphRef.current,
      destroy: () => {
        contextMenuServiceRef.current?.destroy();
        contextMenuServiceRef.current = null;
        graphRef.current?.destroy();
        graphRef.current = null;
        forceRender();
      },
      reset: (newSnapshot) => {
        contextMenuServiceRef.current?.destroy();
        contextMenuServiceRef.current = null;
        graphRef.current?.destroy();
        if (!containerRef.current) return;
        const graph = new WorkflowGraph({
          container: containerRef.current,
          ...config,
        });
        if (newSnapshot) graph.hydrateFromSnapshot(newSnapshot);
        graphRef.current = graph;
        contextMenuServiceRef.current = new ContextMenuService(graph, { showWarning, showSuccess });
        forceRender();
        onReady?.(graph);
      },
    }),
    [config, onReady, showWarning, showSuccess],
  );

  const contextValue: GraphEditorContextValue | null = graphRef.current
    ? { graph: graphRef.current, containerRef }
    : null;

  return (
    <GraphEditorContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        className={containerClassName}
        style={containerStyle ?? { display: 'contents' }}
      />
      {children}
    </GraphEditorContext.Provider>
  );
});
